
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '../../filterables/StackedBarChartFilterable.vue';
import FilterWidget from '../shared/FilterWidget.vue';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import { DataViewFormatter } from '../../elements/charts/helpers/dataViewFormatter';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import moment from 'moment';
import gdbx from '@/store/modules/gdbx';
import { GobiColor } from '@/helpers/color';

@Component({
	components: {
		StackedBarChartFilterable,
		SelectFilter,
	},
})
export default class ProjectSummaryBarChart extends FilterWidget {
	@Prop({ default: () => '' }) public readonly selectedProjectCode!: string;

	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public ytdCost = 0;
	public ytdCollection = 0;
	public ytdBilling = 0;
	public currentMonthCost = 0;
	public currentMonthBilling = 0;
	public currentMonthCollection = 0;
	public totalCollection = 0;
	public totalCost = 0;
	public totalBilling = 0;

	public selected: string = 'total' || null;
	public series: echarts.ECharts[] | any = [];

	public xAxisData = ['Cost', 'Billing', 'Collection'].reverse();

	public get dataViewFormatter() {
		return DataViewFormatter.horizontalBarChart;
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get selectionList(): ValueText[] {
		const list = [
			{ text: 'Current Month', value: 'currentMonth' },
			{ text: 'This Year', value: 'thisYear' },
			{ text: 'Total', value: 'total' },
		];

		return list;
	}

	public get expensiveHook() {
		const { selectedProjectCode, selectedAsOfDate, selected } = this;
		return JSON.stringify([selectedProjectCode, selectedAsOfDate, selected]);
	}

	public async expensiveCalc() {
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const cfTable = getTableName('projectCashFlow');
		const projectCode = escapeStr(this.selectedProjectCode);

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(
			['projectCashFlow'],
		);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		const ytdYear = moment(this.selectedAsOfDate).year();
		const currentMonth =
			moment(this.selectedAsOfDate).startOf('month').month() + 1;

		const costExpr = `(TRY_CAST("STOCKISSUE" AS DOUBLE)`
			+ ` + TRY_CAST("STOCKRETURN" AS DOUBLE)`
			+ ` + TRY_CAST("OTHERSTOCK" AS DOUBLE)`
			+ ` + TRY_CAST("EXPENSES" AS DOUBLE)`
			+ ` + TRY_CAST("SUBCON" AS DOUBLE)`
			+ ` + TRY_CAST("LABOUR" AS DOUBLE))`;
		const billingExpr = `(TRY_CAST("CUSTOMERINVOICE" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERDEBIT" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERCREDIT" AS DOUBLE))`;
		const collectionExpr = `(TRY_CAST("CUSTOMERPAYMENT" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERCONTRA" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERREFUND" AS DOUBLE))`;

		const cfResult = await queryHead.rawQuery(`
			SELECT
				SUM(CASE WHEN yr = ${ytdYear} AND mo <= ${currentMonth}
					THEN cost ELSE 0 END) as ytdCost,
				SUM(CASE WHEN yr = ${ytdYear} AND mo <= ${currentMonth}
					THEN billing ELSE 0 END) as ytdBilling,
				SUM(CASE WHEN yr = ${ytdYear} AND mo <= ${currentMonth}
					THEN collection ELSE 0 END) as ytdCollection,
				SUM(CASE WHEN yr = ${ytdYear} AND mo = ${currentMonth}
					THEN cost ELSE 0 END) as cmCost,
				SUM(CASE WHEN yr = ${ytdYear} AND mo = ${currentMonth}
					THEN billing ELSE 0 END) as cmBilling,
				SUM(CASE WHEN yr = ${ytdYear} AND mo = ${currentMonth}
					THEN collection ELSE 0 END) as cmCollection,
				SUM(CASE WHEN yr < ${ytdYear}
					OR (yr = ${ytdYear} AND mo <= ${currentMonth})
					THEN cost ELSE 0 END) as totalCost,
				SUM(CASE WHEN yr < ${ytdYear}
					OR (yr = ${ytdYear} AND mo <= ${currentMonth})
					THEN billing ELSE 0 END) as totalBilling,
				SUM(CASE WHEN yr < ${ytdYear}
					OR (yr = ${ytdYear} AND mo <= ${currentMonth})
					THEN collection ELSE 0 END) as totalCollection
			FROM (
				SELECT TRY_CAST("YEAR" AS INT) as yr,
					TRY_CAST("MONTH" AS INT) as mo,
					${costExpr} as cost,
					${billingExpr} as billing,
					${collectionExpr} as collection
				FROM ${cfTable}
				WHERE "PROJECT" = '${projectCode}'
			) sub
		`);
		this.checkInterrupted(jobId);

		const cf = cfResult[0] || {};
		this.ytdCost = Number(cf.ytdCost) || 0;
		this.ytdBilling = Number(cf.ytdBilling) || 0;
		this.ytdCollection = Number(cf.ytdCollection) || 0;
		this.currentMonthCost = Number(cf.cmCost) || 0;
		this.currentMonthBilling = Number(cf.cmBilling) || 0;
		this.currentMonthCollection = Number(cf.cmCollection) || 0;
		this.totalCost = Number(cf.totalCost) || 0;
		this.totalBilling = Number(cf.totalBilling) || 0;
		this.totalCollection = Number(cf.totalCollection) || 0;

		let seriesData: number[] = [];

		switch (this.selected) {
			case 'total':
				seriesData.push(this.totalCollection);
				seriesData.push(this.totalBilling);
				seriesData.push(this.totalCost);
				break;
			case 'thisYear':
				seriesData.push(this.ytdCollection);
				seriesData.push(this.ytdBilling);
				seriesData.push(this.ytdCost);
				break;
			case 'currentMonth':
				seriesData.push(this.currentMonthCollection);
				seriesData.push(this.currentMonthBilling);
				seriesData.push(this.currentMonthCost);
				break;
			default:
				seriesData = [];
		}

		const selectedName = this.selected
			? this.selectionList.filter((vt) => vt.value === this.selected)[0].text
			: '';

		this.series = [
			{
				name: selectedName,
				data: seriesData.map((item, index) => ({
					value: item,
					itemStyle: {
						color:
							index === 0
								? GobiColor.TEAL
								: index === 1
								? GobiColor.YELLOW
								: GobiColor.PINK,
					},
				})),
				type: 'bar',
			},
		];

		this.generateLoadingText(1);

		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
